import { useState } from "react";
import { putActivityCategoriesForTenant } from "../../../../api/activityCategories";
import { reportError } from "../../../../api/errors";
import { useAdminData } from "../../AdminDataContext";
import { activityCategoryStore, type ActivityCategory } from "../../utils/activityCategoryStore";

const ICONS = [
  "ri-football-line", "ri-group-line", "ri-building-line", "ri-star-smile-line",
  "ri-run-line", "ri-heart-line", "ri-trophy-line", "ri-calendar-event-line",
  "ri-flag-line", "ri-gamepad-line", "ri-megaphone-line", "ri-music-2-line",
];

export default function ActivityCategoryConfig() {
  const { tenantLegacyId, reload } = useAdminData();
  const [categories, setCategories] = useState<ActivityCategory[]>(() => activityCategoryStore.getAll());
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", icon: "ri-calendar-event-line", description: "" });
  const [editCat, setEditCat] = useState<Partial<ActivityCategory>>({});
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const refresh = () => setCategories(activityCategoryStore.getAll());

  const persistCategories = async (): Promise<boolean> => {
    try {
      const next = await putActivityCategoriesForTenant(tenantLegacyId, activityCategoryStore.getAll());
      activityCategoryStore.replaceFromApi(next);
      refresh();
      void reload({ silent: true });
      return true;
    } catch (e) {
      reportError(e);
      void reload({ silent: true });
      return false;
    }
  };

  const handleToggle = (id: string) => {
    activityCategoryStore.toggleEnabled(id);
    refresh();
    void persistCategories();
  };

  const handleDelete = (id: string) => {
    void (async () => {
      activityCategoryStore.remove(id);
      refresh();
      setDeleteConfirmId(null);
      await persistCategories();
    })();
  };

  const handleAdd = () => {
    void (async () => {
      if (!newCat.name.trim()) return;
      activityCategoryStore.add({
        id: `cat${Date.now()}`,
        name: newCat.name.trim(),
        icon: newCat.icon,
        enabled: true,
        description: newCat.description.trim(),
      });
      setNewCat({ name: "", icon: "ri-calendar-event-line", description: "" });
      setShowAdd(false);
      refresh();
      await persistCategories();
    })();
  };

  const handleSaveEdit = () => {
    void (async () => {
      if (!editingId) return;
      activityCategoryStore.update(editingId, editCat);
      setEditingId(null);
      setEditCat({});
      refresh();
      await persistCategories();
    })();
  };

  const openEdit = (cat: ActivityCategory) => {
    setEditingId(cat.id);
    setEditCat({ name: cat.name, icon: cat.icon, description: cat.description });
  };

  const handleSave = () => {
    void (async () => {
      if (await persistCategories()) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    })();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-semibold text-gray-800">活动分类字典</h3>
          <p className="text-xs text-gray-400 mt-0.5">配置活动类型，创建活动时可从此列表选择</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>新增分类
          </button>
          <button
            onClick={handleSave}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap ${
              saved ? "bg-green-500 text-white" : "bg-red-600 text-white hover:bg-red-700"
            }`}
          >
            <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
            {saved ? "已保存" : "保存配置"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-4 text-xs text-amber-700">
        <i className="ri-lightbulb-line flex-shrink-0"></i>
        <span>启用的分类将显示在「创建活动」的活动类型选择中。关闭某分类不会影响已创建的活动。</span>
      </div>

      <div className="space-y-2">
        {categories.map((cat) => (
          <div
            key={cat.id}
            className={`bg-white rounded-xl border p-4 flex items-center gap-3 transition-all ${
              !cat.enabled ? "opacity-50 border-gray-100" : "border-gray-100"
            }`}
          >
            <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-xl flex-shrink-0">
              <i className={`${cat.icon} text-red-500 text-lg`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-800">{cat.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${cat.enabled ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}>
                  {cat.enabled ? "启用" : "已禁用"}
                </span>
              </div>
              {cat.description && (
                <p className="text-xs text-gray-400 mt-0.5 truncate">{cat.description}</p>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => openEdit(cat)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"
              >
                <i className="ri-edit-line text-sm"></i>
              </button>
              <button
                onClick={() => setDeleteConfirmId(cat.id)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer rounded-lg hover:bg-red-50"
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
              <button
                onClick={() => handleToggle(cat.id)}
                className={`relative rounded-full transition-colors cursor-pointer flex-shrink-0 ${cat.enabled ? "bg-red-500" : "bg-gray-300"}`}
                style={{ width: 40, height: 22 }}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${cat.enabled ? "left-5" : "left-0.5"}`}
                  style={{ width: 16, height: 16 }}
                />
              </button>
            </div>
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="text-center py-10">
          <i className="ri-folder-open-line text-3xl text-gray-200 block mb-2"></i>
          <p className="text-sm text-gray-400">暂无活动分类</p>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-72">
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 text-center mb-2">删除活动分类</h3>
            <p className="text-sm text-gray-500 text-center mb-5 leading-relaxed">
              删除后，该分类将从创建活动的选项中移除。已使用此分类的活动不受影响。
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteConfirmId)} className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-[420px]">
            <h3 className="font-bold text-gray-800 mb-5">新增活动分类</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">分类名称 *</label>
                <input
                  type="text"
                  value={newCat.name}
                  onChange={(e) => setNewCat((p) => ({ ...p, name: e.target.value }))}
                  placeholder="如：球迷联谊"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">图标</label>
                <div className="flex flex-wrap gap-2">
                  {ICONS.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setNewCat((p) => ({ ...p, icon }))}
                      className={`w-9 h-9 flex items-center justify-center rounded-xl border-2 cursor-pointer transition-all ${
                        newCat.icon === icon ? "border-red-500 bg-red-50 text-red-600" : "border-gray-100 text-gray-500 hover:border-gray-200"
                      }`}
                    >
                      <i className={`${icon} text-lg`}></i>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">描述说明</label>
                <input
                  type="text"
                  value={newCat.description}
                  onChange={(e) => setNewCat((p) => ({ ...p, description: e.target.value }))}
                  placeholder="简短描述分类用途"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleAdd} disabled={!newCat.name.trim()} className="px-4 py-2 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer disabled:opacity-40 whitespace-nowrap">添加分类</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-[420px]">
            <h3 className="font-bold text-gray-800 mb-5">编辑活动分类</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">分类名称 *</label>
                <input
                  type="text"
                  value={editCat.name ?? ""}
                  onChange={(e) => setEditCat((p) => ({ ...p, name: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">图标</label>
                <div className="flex flex-wrap gap-2">
                  {ICONS.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setEditCat((p) => ({ ...p, icon }))}
                      className={`w-9 h-9 flex items-center justify-center rounded-xl border-2 cursor-pointer transition-all ${
                        editCat.icon === icon ? "border-red-500 bg-red-50 text-red-600" : "border-gray-100 text-gray-500 hover:border-gray-200"
                      }`}
                    >
                      <i className={`${icon} text-lg`}></i>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">描述说明</label>
                <input
                  type="text"
                  value={editCat.description ?? ""}
                  onChange={(e) => setEditCat((p) => ({ ...p, description: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setEditingId(null)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleSaveEdit} className="px-4 py-2 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap">保存修改</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
